import type {
  WorkoutExerciseRecord,
  WorkoutExerciseDimensionRecord,
  WorkoutSetRecord,
  WorkoutSetValueRecord,
} from '~/powersync/generated/schema';
import { SetOrdering } from './set-ordering';

/**
 * The prior finished session's exercise data, used to clone sets + values into
 * the newly added exercise (mirrors "the previous session's sets show up").
 */
export interface PriorSession {
  /** The prior exercise's sets (any order — re-sorted by sortIndex here). */
  sets: WorkoutSetRecord[];
  /** All set values belonging to {@link PriorSession.sets}. */
  setValues: WorkoutSetValueRecord[];
  /** The prior exercise's dimensions (to map old dimension id → name). */
  dimensions: WorkoutExerciseDimensionRecord[];
}

export interface BuildExerciseInput {
  supersetId: string;
  workoutId: string;
  sportActivity: { id: string; defaultDimensions: string | null };
  /** The new exercise's sortIndex within its superset. */
  sortIndex: number;
  /** Prior session to clone from, or null/undefined for a first-time exercise. */
  priorSession?: PriorSession | null;
}

export interface BuildExerciseOptions {
  /** Mints a fresh uuid; injected so the build stays pure + testable. */
  generateId: () => string;
  /** ISO timestamp stamped onto every created record. */
  now: string;
}

/**
 * The partial local record graph produced when a single exercise is added: the
 * exercise plus its dimensions, sets and set values. There is no workout /
 * superset here — the caller owns those.
 */
export interface ExerciseGraph {
  exercise: WorkoutExerciseRecord;
  dimensions: WorkoutExerciseDimensionRecord[];
  sets: WorkoutSetRecord[];
  setValues: WorkoutSetValueRecord[];
}

/**
 * Pure builder for the record graph of a freshly added workout exercise.
 *
 * Extracted from `WorkoutMutations.createExerciseForSuperset` (mirroring
 * `WorkoutCloner`) so the whole graph is constructed in memory and then written
 * in a single transaction — the exercise, its dimensions and all of its sets
 * (with copied values) appear atomically, instead of trickling in via dozens of
 * separate PowerSync writes.
 *
 * When a prior finished session is supplied, its sets are cloned **ordered by
 * `sortIndex`** (deterministically, via {@link SetOrdering}) and re-indexed
 * `0..n`, and each prior value is mapped onto the new exercise's dimension of
 * the same name. Otherwise a single empty set is created.
 */
export class ExerciseBuilder {
  static build(
    input: BuildExerciseInput,
    opts: BuildExerciseOptions,
  ): ExerciseGraph {
    const { generateId, now } = opts;
    const exerciseId = generateId();

    const exercise: WorkoutExerciseRecord = {
      id: exerciseId,
      supersetId: input.supersetId,
      sportActivityId: input.sportActivity.id,
      definedDimensions: input.sportActivity.defaultDimensions,
      workoutId: input.workoutId,
      sortIndex: input.sortIndex,
      note: null,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    // Parse the PostgreSQL array format ("{REPS,WEIGHT}") into dimension rows.
    const dimensions: WorkoutExerciseDimensionRecord[] =
      ExerciseBuilder.parseDimensionNames(
        input.sportActivity.defaultDimensions,
      ).map((dimension) => ({
        id: generateId(),
        exerciseId,
        dimension,
        unit: 'NONE',
        workoutId: input.workoutId,
        createdAt: now,
        updatedAt: now,
      }));

    if (!input.priorSession || input.priorSession.sets.length === 0) {
      // First-time exercise: a single empty set, no values.
      const sets: WorkoutSetRecord[] = [
        {
          id: generateId(),
          exerciseId,
          workoutId: input.workoutId,
          sortIndex: 0,
          completedAt: null,
          createdAt: now,
          updatedAt: now,
        },
      ];
      return { exercise, dimensions, sets, setValues: [] };
    }

    // Clone the prior session's sets + values.
    const newDimIdByName = new Map(dimensions.map((d) => [d.dimension, d.id]));
    const oldDimNameById = new Map(
      input.priorSession.dimensions.map((d) => [d.id, d.dimension]),
    );
    const priorValuesBySetId = new Map<string, WorkoutSetValueRecord[]>();
    for (const value of input.priorSession.setValues) {
      const bucket = priorValuesBySetId.get(value.setId ?? '');
      if (bucket) {
        bucket.push(value);
      } else {
        priorValuesBySetId.set(value.setId ?? '', [value]);
      }
    }

    const sets: WorkoutSetRecord[] = [];
    const setValues: WorkoutSetValueRecord[] = [];

    SetOrdering.sort(input.priorSession.sets).forEach((priorSet, index) => {
      const setId = generateId();
      sets.push({
        id: setId,
        exerciseId,
        workoutId: input.workoutId,
        sortIndex: index,
        completedAt: null,
        createdAt: now,
        updatedAt: now,
      });

      for (const priorValue of priorValuesBySetId.get(priorSet.id) ?? []) {
        const dimensionName = oldDimNameById.get(
          priorValue.exerciseDimensionId ?? '',
        );
        const newDimensionId = dimensionName
          ? newDimIdByName.get(dimensionName)
          : undefined;
        if (!newDimensionId) continue;
        setValues.push({
          id: generateId(),
          setId,
          exerciseDimensionId: newDimensionId,
          value: priorValue.value,
          workoutId: input.workoutId,
          createdAt: now,
          updatedAt: now,
        });
      }
    });

    return { exercise, dimensions, sets, setValues };
  }

  /** Parse a PostgreSQL text-array string ("{REPS,WEIGHT}") into names. */
  private static parseDimensionNames(
    defaultDimensions: string | null,
  ): string[] {
    if (!defaultDimensions) return [];
    return defaultDimensions
      .replace(/[{}]/g, '')
      .split(',')
      .map((dim) => dim.trim())
      .filter((dim) => dim.length > 0);
  }
}
